import React, { useEffect } from 'react';
import PricingSection from '../../components/Home/PricingSection';

const CRMLanding: React.FC = () => {
  useEffect(() => {
    const title = 'Customer CRM | Explanatum';
    const desc = 'Unify pipeline, outreach, and customer intelligence with an AI-connected CRM that syncs with your business consultant.';
    const url = 'https://explanatum.co.uk/modules/customer-crm';
    document.title = title;

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    const setOg = (property: string, content: string) => {
      let el = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('property', property);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', desc);
    setOg('og:title', title);
    setOg('og:description', desc);
    setOg('og:url', url);

    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', url);
  }, []);

  const track = (event: string, params: Record<string, unknown> = {}) => {
    try {
      const w = window as unknown as { gtag?: (...args: unknown[]) => void };
      if (w && typeof w.gtag === 'function') w.gtag('event', event, params);
    } catch {}
  };

  return (
    <main className="bg-white" role="main">
      {/* Hero */}
      <section className="relative overflow-hidden" role="region" aria-label="CRM overview">
        <div className="max-w-7xl mx-auto px-6 pt-20 pb-24 lg:pt-28 lg:pb-32 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-4 py-1 text-sm font-semibold text-blue-700">
                <span className="inline-flex h-2 w-2 rounded-full bg-blue-600" />
                AI Customer CRM
              </span>
              <h1 className="mt-6 text-4xl font-extrabold tracking-tight text-gray-900 sm:text-5xl lg:text-6xl">
                Your pipeline, outreach, and tasks in one AI-powered workspace.
                <span className="mt-2 block text-gray-900">
                  Syncs with Explanatum consultants automatically.
                </span>
              </h1>
              <p className="mt-6 text-lg text-gray-600 max-w-xl">
                Manage contacts, deals, meetings, and follow-ups without losing context. The CRM talks to your AI Business Consultant so every conversation, plan, and outreach action stays aligned with revenue goals.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <a
                  href="#pricing"
                  onClick={() => track('click_crm_cta', { placement: 'hero' })}
                  className="inline-flex items-center justify-center rounded-full bg-blue-600 hover:bg-blue-700 transition-colors px-6 py-3 text-base font-semibold text-white shadow-lg transition hover:scale-[1.02]"
                >
                  Start free trial
                </a>
                <a
                  href="#compare"
                  className="inline-flex items-center justify-center rounded-full border border-slate-500/50 px-6 py-3 text-base font-semibold text-gray-600 hover:bg-blue-50 transition-colors"
                >
                  See how it compares
                </a>
              </div>
              <div className="mt-8 flex flex-wrap items-center gap-4 text-sm text-gray-500">
                <span className="flex items-center gap-2">
                  <span className="inline-flex h-2 w-2 rounded-full bg-emerald-400" />
                  Unified contacts, deals, and tasks
                </span>
                <span className="flex items-center gap-2">
                  <span className="inline-flex h-2 w-2 rounded-full bg-sky-400" />
                  AI follow-up suggestions in context
                </span>
                <span className="flex items-center gap-2">
                  <span className="inline-flex h-2 w-2 rounded-full bg-blue-600" />
                  Syncs with WhatsApp, email, and social posts
                </span>
              </div>
            </div>
            <aside className="relative" aria-label="CRM workspace features">
              <div className="absolute -top-6 -left-6 h-32 w-32 rounded-full bg-blue-50 blur-3xl opacity-40" aria-hidden="true" />
              <div className="absolute -bottom-10 -right-8 h-36 w-36 rounded-full bg-cyan-50 blur-3xl opacity-30" aria-hidden="true" />
              <nav className="relative rounded-3xl border border-gray-200 bg-white shadow-2xl backdrop-blur" aria-labelledby="crm-toc-title">
                <div className="px-6 py-5 border-b border-gray-200">
                  <h2 id="crm-toc-title" className="text-lg font-semibold text-gray-900">Everything your team needs</h2>
                  <p className="text-sm text-white/90 mt-1">AI-assisted workflows that keep sellers informed and proactive.</p>
                </div>
                <ol className="p-6 space-y-2 list-decimal list-inside text-gray-900">
                  <li className="text-gray-900">Pipeline dashboards & revenue forecasts</li>
                  <li className="text-gray-900">Contacts, companies, and full interaction history</li>
                  <li className="text-gray-900">Activity feed with WhatsApp, email, and call notes</li>
                  <li className="text-gray-900">Automated follow-up sequences & task queues</li>
                  <li className="text-gray-900">Meeting scheduler and calendar sync</li>
                  <li className="text-gray-900">Collaborative notes, files, and playbooks</li>
                  <li className="text-gray-900">AI business consultant suggestions in-record</li>
                </ol>
              </nav>
            </aside>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section aria-label="CRM benefits" className="bg-white py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center">
            <p className="mt-4 text-base text-gray-500">Replace siloed tools with a selling workspace that knows your customers, market context, and follow-up cadence.</p>
          </div>
          <div className="mt-14 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {[{
              title: 'Unified customer timeline',
              body: 'Calls, WhatsApp messages, emails, meeting notes, and AI consultant insights are logged automatically so sellers always see the full story.'
            }, {
              title: 'AI-assisted follow-up',
              body: 'Suggested tasks, email drafts, and WhatsApp replies appear in-line based on recent activity, pipeline stage, and consultant advice.'
            }, {
              title: 'Pipeline intelligence',
              body: 'Live dashboards surface risked deals, forecast changes, and marketing conversion trends without leaving the CRM workspace.'
            }, {
              title: 'Cross-channel capture',
              body: 'Sync meetings, documents, forms, and files so every stakeholder has the context they need to keep momentum.'
            }, {
              title: 'Playbooks & workflows',
              body: 'Trigger automations for onboarding, renewals, and expansions while the AI consultant checks progress and nudges owners.'
            }, {
              title: 'Native with Explanatum suite',
              body: 'Launch campaigns, generate collateral, and push outcomes straight from the CRM with no integrations to wrangle.'
            }].map((item) => (
              <article key={item.title} className="rounded-3xl border border-gray-200 bg-white p-6 shadow-lg shadow-lg">
                <h3 className="text-xl font-semibold text-gray-900">{item.title}</h3>
                <p className="mt-3 text-sm text-gray-500">{item.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section id="compare" aria-label="Comparison" className="relative overflow-hidden bg-white py-20">
        <div className="max-w-6xl mx-auto px-6 relative">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900">Why teams upgrade from HubSpot and Zoho</h2>
            <p className="mt-4 text-base text-gray-500">Legacy CRMs manage records; Explanatum closes the loop between pipeline, execution, and AI guidance.</p>
          </div>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {[{
              title: 'Explanatum Customer CRM',
              points: [
                'Single workspace for contacts, deals, activities, and consultant advice',
                'AI-generated follow-ups, decks, and WhatsApp replies inside each record',
                'Pipeline dashboards enriched with marketing and finance signals',
                'WhatsApp, email, voice, and social posts log automatically',
                'Native link to Business Consultant for playbooks and coaching'
              ],
              accent: 'border-gray-200 bg-gray-50 text-white'
            }, {
              title: 'HubSpot Sales Hub',
              points: [
                'Great for inbound marketing, but AI insights live in separate panels',
                'Complex tiered pricing for sequences, custom reporting, and workspace limits',
                'Requires paid add-ons or ops tools to sync WhatsApp and bespoke automations',
                'Consultant-style guidance relies on manual playbooks and reporting meetings',
                'Switching between Sales, Marketing, and Service hubs slows teams down'
              ],
              accent: 'border-gray-200 bg-gray-50 text-white'
            }, {
              title: 'Zoho CRM & extensions',
              points: [
                'Feature-rich but fragmented across Zoho apps and custom Deluge scripts',
                'AI assistant Zia focuses on analytics—no deep link to external messaging',
                'Requires admin time to stitch marketing, finance, and support context',
                'Limited real-time automation for WhatsApp or consultant-style coaching',
                'Steep learning curve for teams moving beyond basic contact management'
              ],
              accent: 'border-gray-200 bg-gray-50 text-white'
            }].map((column) => (
              <article key={column.title} className={`rounded-3xl border ${column.accent} p-6 shadow-xl shadow-lg`}>
                <h3 className="text-lg font-semibold text-gray-900">{column.title}</h3>
                <ul className="mt-5 space-y-3 text-sm text-gray-900">
                  {column.points.map((point) => (
                    <li key={point} className="flex items-start gap-3 text-gray-900">
                      <span className="mt-1 inline-flex h-1.5 w-1.5 rounded-full bg-white" />
                      <span className="text-gray-900">{point}</span>
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
          <div className="mt-14 rounded-3xl border border-gray-200 bg-sky-400/10 p-8 text-center backdrop-blur">
            <h3 className="text-2xl font-semibold text-gray-900">“What’s the next best action for this deal?”</h3>
            <p className="mt-3 text-base text-gray-600">Explanatum reads your pipeline, consults the AI Business Consultant, and responds with the exact outreach, assets, and reminders required.</p>
            <div className="mt-6 flex flex-wrap justify-center gap-4">
              <a
                href="#pricing"
                onClick={() => track('click_crm_cta', { placement: 'compare' })}
                className="inline-flex items-center justify-center rounded-full bg-gradient-to-r from-sky-500 via-indigo-500 to-purple-500 px-6 py-3 text-base font-semibold text-white shadow-lg shadow-sky-500/30 hover:scale-[1.02]"
              >
                Run a pilot
              </a>
              <a
                href="/modules/business-consultant"
                className="inline-flex items-center justify-center rounded-full border border-slate-500/60 px-6 py-3 text-base font-semibold text-gray-600 hover:border-slate-300/70"
              >
                Pair with the Consultant
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Scenarios */}
      <section aria-label="CRM scenarios" className="bg-white py-20">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-gray-900">How teams use the CRM day to day</h2>
            <p className="mt-4 text-base text-gray-500">Pick a scenario and Explanatum orchestrates the prep, follow-up, and reporting without extra tools.</p>
          </div>
          <div className="mt-12 grid gap-6 lg:grid-cols-3">
            {[{
              title: 'Daily stand-up insights',
              body: 'Start the day with AI-generated briefings: deals at risk, meetings, and the outreach needed to stay on target.'
            }, {
              title: 'Investor & board updates',
              body: 'Create pipeline snapshots, attach notes, and push a plan of action straight to the Business Consultant for execution.'
            }, {
              title: 'Multi-channel outreach',
              body: 'From a deal record, send WhatsApp, email, or social messages using consultant-crafted copy and automatically log responses.'
            }].map((scenario) => (
              <article key={scenario.title} className="rounded-3xl border border-gray-200 bg-white p-6 shadow-lg">
                <h3 className="text-xl font-semibold text-gray-900">{scenario.title}</h3>
                <p className="mt-3 text-sm text-gray-500">{scenario.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" aria-label="Pricing" className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <PricingSection />
        </div>
      </section>
    </main>
  );
};

export default CRMLanding;
